/**
 * Pure data transforms that let "template mode" reuse the Thời khoá biểu
 * grid untouched — see docs/superpowers/sdd/2026-08-24-schedule-templates.
 * No React, no fetch: both functions are data-in/data-out.
 */

import type { ClassSlotWithDetail, ScheduleTemplateEntryDetail, SlotMember } from '@repo/shared-types';

function toMember(e: ScheduleTemplateEntryDetail): SlotMember {
  return {
    enrollmentId: e.enrollmentId,
    studentId: e.studentId ?? '',
    studentName: e.studentName ?? '',
    // A hard orphan (enrollment no longer exists at all) carries no status —
    // 'ENDED' is the closest real SlotMember status and keeps the card
    // rendering rather than crashing on a value the type doesn't allow.
    status: e.enrollmentStatus ?? 'ENDED',
  };
}

/** Rebuild the slot list the grid renders so that, in template mode, each
 *  card's `members` come from the TEMPLATE instead of SlotEnrollment.
 *
 *  Returns real ClassSlotWithDetail objects with `members`/`memberCount`
 *  swapped, so TimetableSlotCard, MoveModeSlotCard and slotMoveDraft all
 *  work unchanged — template mode adds no second rendering path.
 *
 *  Archived slots that still carry entries are INCLUDED (with
 *  `templateOrphan: true`) so the "slot đã lưu trữ" chip has a card to
 *  live on; the grid renders them non-interactive.
 */
export function overlayTemplateOnSlots(
  activeSlots: ClassSlotWithDetail[],
  archivedSlots: ClassSlotWithDetail[],
  entries: ScheduleTemplateEntryDetail[],
): (ClassSlotWithDetail & { templateOrphan: boolean })[] {
  const entriesBySlot = new Map<string, ScheduleTemplateEntryDetail[]>();
  for (const e of entries) {
    const list = entriesBySlot.get(e.slotId);
    if (list) list.push(e);
    else entriesBySlot.set(e.slotId, [e]);
  }

  const active = activeSlots.map((slot) => {
    const members = (entriesBySlot.get(slot.id) ?? []).map(toMember);
    return { ...slot, members, memberCount: members.length, templateOrphan: false };
  });

  // Archived slots with no template entries simply don't appear — nothing
  // to warn about and nothing for a card to show.
  const orphans = archivedSlots
    .filter((slot) => (entriesBySlot.get(slot.id) ?? []).length > 0)
    .map((slot) => {
      const members = (entriesBySlot.get(slot.id) ?? []).map(toMember);
      return { ...slot, members, memberCount: members.length, templateOrphan: true };
    });

  return [...active, ...orphans];
}

/** The move-mode confirm, expressed on entries instead of memberships:
 *  every (fromSlotId, enrollmentId) pair becomes (toSlotId, enrollmentId).
 *  Idempotent and duplicate-safe — the result is deduped on the pair.
 *
 *  Takes (and returns) only the two fields it needs, so a caller can pass
 *  `ScheduleTemplateEntryDetail[]` straight through — TS structurally
 *  accepts the wider array — and get back exactly the shape
 *  `PutScheduleTemplateEntriesInput` expects, with no extra fields to strip. */
export function applyMoveToEntries(
  entries: { slotId: string; enrollmentId: string }[],
  move: { fromSlotId: string; toSlotId: string; enrollmentIds: string[] },
): { slotId: string; enrollmentId: string }[] {
  const moving = new Set(move.enrollmentIds);
  const mapped = entries.map((e) =>
    e.slotId === move.fromSlotId && moving.has(e.enrollmentId)
      ? { slotId: move.toSlotId, enrollmentId: e.enrollmentId }
      : { slotId: e.slotId, enrollmentId: e.enrollmentId },
  );

  // Dedupe on (slotId, enrollmentId) — a doubled id, or a pair already
  // sitting at the target before the move, must not survive as two rows.
  const seen = new Map<string, { slotId: string; enrollmentId: string }>();
  for (const e of mapped) seen.set(`${e.slotId}:${e.enrollmentId}`, e);
  return Array.from(seen.values());
}

/** Slot-detail dialog's remove-in-template-mode: drop one (slotId,
 *  enrollmentId) pair from a template's entries. Same minimal pair shape as
 *  applyMoveToEntries, for the same reason — a caller can pass
 *  `ScheduleTemplateEntryDetail[]` straight through. */
export function removeEntryFromEntries(
  entries: { slotId: string; enrollmentId: string }[],
  target: { slotId: string; enrollmentId: string },
): { slotId: string; enrollmentId: string }[] {
  return entries
    .filter((e) => !(e.slotId === target.slotId && e.enrollmentId === target.enrollmentId))
    .map((e) => ({ slotId: e.slotId, enrollmentId: e.enrollmentId }));
}

/** Slot-detail dialog's add-in-template-mode: add one (slotId, enrollmentId)
 *  pair to a template's entries. Dedupe-safe on the pair, same as
 *  applyMoveToEntries — picking a student already in the template's entries
 *  for this slot is a no-op rather than a duplicate row. */
export function addEntryToEntries(
  entries: { slotId: string; enrollmentId: string }[],
  addition: { slotId: string; enrollmentId: string },
): { slotId: string; enrollmentId: string }[] {
  const seen = new Map<string, { slotId: string; enrollmentId: string }>();
  for (const e of entries) seen.set(`${e.slotId}:${e.enrollmentId}`, { slotId: e.slotId, enrollmentId: e.enrollmentId });
  seen.set(`${addition.slotId}:${addition.enrollmentId}`, addition);
  return Array.from(seen.values());
}
